import { CircleCheck, Eye, EyeOff, X } from "@icons";
import classNames from "classnames";
import { ReactNode, Ref, useRef, useState } from "react";

import { TextFieldProps } from "../_shared/field";
import FieldLabel from "../_shared/field-label";
import FieldMessage from "../_shared/field-message";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export interface InputProps extends Omit<TextFieldProps, "value"> {
  /** Committed value (controlled). Numbers are accepted for numeric fields. */
  value?: string | number;
  type?: React.HTMLInputTypeAttribute;
  autoComplete?: string;
  /** Id of a <datalist> providing native autocomplete suggestions. */
  list?: string;
  /** Actions rendered at the right end of the label row (e.g. a prefilled-value indicator). */
  labelActions?: ReactNode;
  /** Native constraints forwarded to the input (number fields). */
  min?: number | string;
  max?: number | string;
  step?: number | string;
  /** Icon rendered inside the control at the left; picks up the focus colour. */
  leadingIcon?: ReactNode;
  /** Static text before the value (e.g. "$"). */
  prefix?: ReactNode;
  /** Static text after the value (e.g. "/hr"). */
  suffix?: ReactNode;
  /** Show an × to clear the value while the field is filled. */
  clearable?: boolean;
  /** Hard character cap. */
  maxLength?: number;
  /** Show a live character counter against `maxLength`. */
  showCount?: boolean;
  /** Validate email format on blur / Enter, flashing a rejection shake. */
  validateEmail?: boolean;
  /** Message shown when `validateEmail` rejects. English default; translate at the call site. */
  invalidEmailMessage?: string;
  /** Force the focus ring (controlled focus / showcase). */
  focused?: boolean;
  /** Ref to the underlying input — used by {@link ControlledInput} to wire react-hook-form's field ref. */
  inputRef?: Ref<HTMLInputElement>;
  /** Notified when the input blurs (react-hook-form integration). */
  onBlur?: () => void;
}

export function Input({
  name,
  label,
  required = false,
  info,
  infoOpen = false,
  value = "",
  onChange = () => {},
  onBlur,
  type = "text",
  placeholder,
  autoComplete,
  list,
  labelActions,
  disabled = false,
  readOnly = false,
  error,
  valid,
  hint,
  defaultBadge,
  size = "md",
  min,
  max,
  step,
  leadingIcon,
  prefix,
  suffix,
  clearable = false,
  maxLength,
  showCount = false,
  validateEmail = false,
  invalidEmailMessage = "Enter a valid email address.",
  focused = false,
  inputRef,
}: InputProps) {
  const [validationError, setValidationError] = useState<string | null>(null);
  const [isFocus, setIsFocus] = useState(false);
  const [shake, setShake] = useState(false);
  const [reveal, setReveal] = useState(false);
  const innerRef = useRef<HTMLInputElement | null>(null);

  const stringValue = value == null ? "" : String(value);
  const isPassword = type === "password";
  const displayError = validationError ?? error ?? undefined;
  const hasError = !!displayError;
  const isValid = !hasError && !!valid;
  const showFocus = focused || isFocus;
  const showClear =
    clearable && stringValue.length > 0 && !disabled && !readOnly;
  const atMax = maxLength != null && stringValue.length >= maxLength;
  const inputType = isPassword && reveal ? "text" : type;

  function flashReject() {
    setShake(true);
    window.setTimeout(() => setShake(false), 340);
  }

  function validate() {
    const trimmed = stringValue.trim();
    if (validateEmail && trimmed && !EMAIL_REGEX.test(trimmed)) {
      setValidationError(invalidEmailMessage);
      flashReject();
      return false;
    }
    if (validationError === invalidEmailMessage) setValidationError(null);
    return true;
  }

  function assignRef(node: HTMLInputElement | null) {
    innerRef.current = node;
    if (typeof inputRef === "function") inputRef(node);
    else if (inputRef)
      (inputRef as React.MutableRefObject<HTMLInputElement | null>).current =
        node;
  }

  return (
    <div className="font-sans w-full">
      <FieldLabel
        htmlFor={name}
        label={label}
        required={required}
        info={info}
        infoOpen={infoOpen}
        labelActions={labelActions}
      />

      <div
        role="presentation"
        onClick={() => {
          if (!disabled && innerRef.current) innerRef.current.focus();
        }}
        className={classNames(
          "flex items-center w-full rounded border transition-[border-color,box-shadow,background]",
          {
            "h-8 px-2 gap-1.5": size === "sm",
            "h-10 px-2.5 gap-2": size === "md",
            "h-[46px] px-3 gap-2.5": size === "lg",
            "border-gray-200": !hasError && !isValid && !showFocus,
            "border-primary-300 ring-2 ring-primary-300/40":
              showFocus && !hasError && !isValid,
            "border-red-500 ring-2 ring-red-500/20": hasError,
            "border-green-600 ring-2 ring-green-600/20": isValid,
            "bg-white cursor-text": !disabled && !readOnly,
            "bg-gray-100 cursor-not-allowed": disabled,
            "bg-[#f5f7fb] cursor-default": readOnly && !disabled,
            "motion-safe:animate-shake": shake,
          },
        )}
      >
        {leadingIcon && (
          <span
            className={classNames(
              "flex items-center shrink-0 transition-colors",
              showFocus ? "text-primary-400" : "text-gray-400",
              size === "sm"
                ? "[&_svg]:w-[15px] [&_svg]:h-[15px]"
                : "[&_svg]:w-[17px] [&_svg]:h-[17px]",
            )}
          >
            {leadingIcon}
          </span>
        )}
        {prefix && (
          <span className="shrink-0 -mr-0.5 text-sm text-gray-500 whitespace-nowrap select-none">
            {prefix}
          </span>
        )}

        <input
          ref={assignRef}
          id={name}
          name={name}
          type={inputType}
          value={stringValue}
          placeholder={placeholder}
          disabled={disabled}
          readOnly={readOnly}
          autoComplete={autoComplete}
          list={list}
          min={min}
          max={max}
          step={step}
          maxLength={maxLength}
          aria-invalid={hasError}
          className={classNames(
            "flex-1 min-w-0 h-full border-0 outline-none bg-transparent text-gray-800 placeholder:text-gray-400 disabled:cursor-not-allowed read-only:cursor-default",
            {
              "text-[13px]": size === "sm",
              "text-sm": size === "md",
              "text-[15px]": size === "lg",
            },
          )}
          onChange={(event) => {
            onChange(event.target.value);
            if (validationError) setValidationError(null);
          }}
          onKeyDown={(event) => {
            if (event.key === "Enter") validate();
          }}
          onFocus={() => setIsFocus(true)}
          onBlur={() => {
            setIsFocus(false);
            validate();
            onBlur?.();
          }}
          onWheel={
            type === "number"
              ? (event) => event.currentTarget.blur()
              : undefined
          }
        />

        {suffix && (
          <span className="shrink-0 -ml-0.5 text-sm text-gray-500 whitespace-nowrap select-none">
            {suffix}
          </span>
        )}

        {showCount && maxLength != null && (
          <span
            className={classNames(
              "shrink-0 text-[11.5px] tabular-nums select-none",
              atMax ? "text-red-500" : "text-gray-400",
            )}
          >
            {stringValue.length}/{maxLength}
          </span>
        )}

        {showClear && (
          <button
            type="button"
            aria-label="Clear"
            onClick={(event) => {
              event.stopPropagation();
              setValidationError(null);
              onChange("");
              innerRef.current?.focus();
            }}
            className="inline-flex items-center justify-center w-[22px] h-[22px] shrink-0 p-0 border-0 bg-transparent text-gray-400 rounded-full cursor-pointer transition-colors hover:bg-gray-100 hover:text-gray-600"
          >
            <X size={15} />
          </button>
        )}

        {isPassword && !disabled && (
          <button
            type="button"
            aria-label={reveal ? "Hide" : "Show"}
            onClick={(event) => {
              event.stopPropagation();
              setReveal((shown) => !shown);
            }}
            className="inline-flex items-center justify-center w-[22px] h-[22px] shrink-0 p-0 border-0 bg-transparent text-gray-400 rounded-full cursor-pointer transition-colors hover:bg-gray-100 hover:text-gray-600"
          >
            {reveal ? <EyeOff size={16} /> : <Eye size={16} />}
          </button>
        )}

        {isValid && !showClear && !isPassword && (
          <span className="flex items-center shrink-0 text-green-600">
            <CircleCheck size={size === "sm" ? 15 : 17} />
          </span>
        )}
      </div>

      <FieldMessage
        error={displayError}
        valid={valid}
        hint={hint}
        defaultBadge={defaultBadge}
        isEmpty={stringValue.length === 0}
      />
    </div>
  );
}

export default Input;
